import { OpenFeature } from '@openfeature/web-sdk';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { FeatureFlagContext } from '@risksmart-app/feature-flags/src/context';
import { toEvaluationContext } from '@risksmart-app/feature-flags/src/context';
import type { FeatureFlagProviderKind } from '@risksmart-app/feature-flags/src/provider-kind';
import type { WebProviderConfig } from '@risksmart-app/feature-flags/src/web/create-web-provider';
import {
  createWebFeatureFlagProvider,
  webProviderConfigSchema,
} from '@risksmart-app/feature-flags/src/web/create-web-provider';
import { toDatadogEnv } from '@risksmart-app/shared/src/utils/datadog-environment';
import * as Sentry from '@sentry/react';

/**
 * Environments where booting on registry defaults is acceptable. Everywhere
 * else, resolving to `in-memory` is treated as a misconfiguration — see
 * `resolvePortalProviderConfig`.
 */
const LOCAL_ENVIRONMENT = 'dev-local';

/**
 * Only an *explicit* `dev-local` is lenient. An absent or empty
 * `REACT_APP_ENVIRONMENT` must NOT be treated as local: `getEnv` throws on
 * `undefined` but happily returns `''`, and build pipelines deliberately bake
 * empty strings for unset vars — so a deployed build that lost the var would
 * otherwise take the lenient branch and resolve `trpc` from registry defaults,
 * silently routing a cut-over org onto the retired GraphQL transport.
 */
const isLocalEnvironment = (): boolean =>
  getEnv('REACT_APP_ENVIRONMENT', true) === LOCAL_ENVIRONMENT;

/**
 * Resolves the OpenFeature provider config from the same `REACT_APP_*` env vars
 * web reads (`packages/web/src/feature-flags/openfeature.ts`), with one
 * deliberate divergence: **the portal refuses to run on registry defaults in a
 * deployed environment.**
 *
 * Web quietly falls back to `in-memory` when the Datadog RUM token is absent,
 * because a web flag defaulting to `false` just hides an unreleased feature. In
 * the portal the only flag is `trpc`, and `false` selects the GraphQL transport
 * being retired — so a silent fallback would route every cut-over org back onto
 * a path we stop validating once an org is cut over (ENG-618). An `in-memory`
 * provider is *healthy*, so it reports no error the gate could catch: the only
 * place to notice is here, at config resolution.
 *
 * The strictness is scoped to deployed stages so a developer without RUM
 * credentials can still boot the portal locally.
 */
const resolvePortalProviderConfig = (): WebProviderConfig => {
  const configuredKind = getEnv('REACT_APP_FEATURE_FLAG_PROVIDER', true);
  const kind = configuredKind || 'in-memory';

  switch (kind) {
    case 'flagd':
      return webProviderConfigSchema.parse({
        kind,
        host: getEnv('REACT_APP_FLAGD_HOST', true) || 'localhost',
        port: getEnv('REACT_APP_FLAGD_PORT', true) || 8013,
        // TLS is env-driven (default off): the local sidecar is plaintext
        // localhost:8013, while a bundle reaching flagd through an ALB uses
        // HTTPS (REACT_APP_FLAGD_TLS="true").
        tls: getEnv('REACT_APP_FLAGD_TLS', true) === 'true',
      });
    case 'datadog': {
      const clientToken = getEnv('REACT_APP_DD_CLIENT_TOKEN', true);

      if (!clientToken) {
        if (!isLocalEnvironment()) {
          throw new Error(
            'Feature flag provider "datadog" is configured but REACT_APP_DD_CLIENT_TOKEN is missing — refusing to resolve flags from registry defaults outside local development.'
          );
        }

        return webProviderConfigSchema.parse({ kind: 'in-memory' });
      }

      return webProviderConfigSchema.parse({
        kind,
        clientToken,
        applicationId: getEnv('REACT_APP_DD_APPLICATION_ID', true) || undefined,
        site: getEnv('REACT_APP_DD_SITE', true) || 'datadoghq.eu',
        // app→prod parity with the server's DD_ENV; `||` (not `??`) so an
        // empty REACT_APP_ENVIRONMENT also falls back.
        env: toDatadogEnv(getEnv('REACT_APP_ENVIRONMENT', true)) || 'unknown',
        service: 'risksmart-third-party-portal',
      });
    }
    default:
      if (kind === 'in-memory' && !isLocalEnvironment()) {
        // Distinguish "nobody set the var" from "somebody asked for
        // in-memory" — both land here (an unset var defaults to `in-memory`),
        // but they need different fixes.
        throw new Error(
          configuredKind
            ? 'Feature flag provider "in-memory" resolves flags from registry defaults — refusing outside local development. Set REACT_APP_FEATURE_FLAG_PROVIDER to "datadog" or "flagd".'
            : 'No feature flag provider is configured (REACT_APP_FEATURE_FLAG_PROVIDER) — refusing to resolve flags from registry defaults outside local development.'
        );
      }

      return webProviderConfigSchema.parse({ kind });
  }
};

let activeProviderKind: FeatureFlagProviderKind | null = null;
let bootstrapError: Error | null = null;

export const getActiveFeatureFlagProviderKind =
  (): FeatureFlagProviderKind | null => activeProviderKind;

/**
 * The error that stopped provider registration, if any. Unlike web — which
 * boots on registry defaults after a failed bootstrap — the portal surfaces
 * this through `FeatureFlagProvider`, which blocks children and renders an
 * error rather than answering `trpc: false` by accident.
 */
export const getFeatureFlagBootstrapError = (): Error | null => bootstrapError;

/**
 * Registers the configured OpenFeature provider. Idempotent — called once at
 * boot from `index.tsx`, guarded so stray re-invocations (tests, StrictMode)
 * are no-ops.
 */
export const initialisePortalFeatureFlags = (): void => {
  if (activeProviderKind !== null || bootstrapError !== null) {
    return;
  }

  try {
    const config = resolvePortalProviderConfig();
    OpenFeature.setProvider(createWebFeatureFlagProvider(config));
    activeProviderKind = config.kind;
  } catch (error) {
    bootstrapError = error instanceof Error ? error : new Error(String(error));
    Sentry.captureException(error, { tags: { component: 'feature-flags' } });
  }
};

/**
 * Test-only reset of the module-level bootstrap state. Production code calls
 * `initialisePortalFeatureFlags` exactly once, so the guards above would
 * otherwise make provider state leak between test cases.
 */
export const resetPortalFeatureFlagsForTesting = (): void => {
  activeProviderKind = null;
  bootstrapError = null;
};

/**
 * Binds the signed-in respondent's organisation onto the global evaluation
 * context (zod-validated; orgId becomes the targeting key). Providers
 * re-evaluate flags against the new context automatically.
 */
export const setFeatureFlagContext = async (
  context: FeatureFlagContext
): Promise<void> => {
  await OpenFeature.setContext(toEvaluationContext(context));
};

/**
 * Clears the global evaluation context (logout / provider unmount) so a
 * signed-out session never evaluates flags against a stale identity.
 */
export const clearFeatureFlagContext = async (): Promise<void> => {
  await OpenFeature.clearContext();
};
